export const company = {
  name: 'Hochbegabt Technology',
  /** German sense of the name. The firm uses it as a standard of hard work. */
  meaning: 'highly gifted',
};

export const navItems = [
  { label: 'Services', to: '/services' },
  { label: 'Approach', to: '/approach' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export const interestOptions = [
  'Custom software development',
  'Application modernization',
  'AI workflow implementation',
  'Prototype to production',
  'Not sure yet',
] as const;

export type InterestOption = (typeof interestOptions)[number];

export const pageTitles: Record<string, string> = {
  '/': company.name,
  '/about': `About — ${company.name}`,
  '/contact': `Contact — ${company.name}`,
  '/services': `Services — ${company.name}`,
  '/approach': `Approach — ${company.name}`,
};

export const hero = {
  eyebrow: 'Custom software development',
  title: 'We design and build software your team can own.',
  lede:
    'New products, modernization of the systems you already run, AI in a production workflow, and prototypes taken through to software a team can maintain.',
};

export type Service = {
  id: string;
  title: string;
  summary: string;
  includes: string[];
  outcome: string;
};

export const services: Service[] = [
  {
    id: 'new',
    title: 'Custom software development',
    summary: 'Design and build a product around a specific business problem, then leave code your team can maintain.',
    includes: [
      'Define the smallest release worth putting in front of users',
      'Design the product around the work, then build it',
      'Hand off the repository, environments, and decisions',
    ],
    outcome: 'A production application your team can run and extend.',
  },
  {
    id: 'existing',
    title: 'Application modernization',
    summary: 'Improve the software you already depend on, without a full rewrite.',
    includes: [
      'Assess the codebase against the work it supports',
      'Refactor the areas that make every release expensive',
      'Ship the changes in the system users already have',
    ],
    outcome: 'A system your team can keep releasing, with the fragile parts isolated.',
  },
  {
    id: 'ai',
    title: 'AI workflow implementation',
    summary: 'Put AI into a workflow people already use, with a person in the loop where a wrong answer is costly.',
    includes: [
      'Choose a use case with a clear owner and a measurable result',
      'Implement it inside the tools the work already lives in',
      'Keep review in the path when the cost of a mistake is high',
    ],
    outcome: 'An AI feature in production, with a way to tell when it is wrong.',
  },
  {
    id: 'repair',
    title: 'Prototype to production',
    summary: 'Take an AI-built prototype that looks finished and make it software a business can run.',
    includes: [
      'Separate what works from what only looks complete',
      'Rebuild the parts that will not hold up in production',
      'Leave a codebase the team can maintain',
    ],
    outcome: 'Production software your team owns, not a demo that fails in real use.',
  },
];

export const approachSteps = [
  {
    title: 'See the work',
    text: 'We learn the system you run and the daily work around it.',
    detail:
      'We read the code and follow one real pass through the work: where a release stalls, where a person repeats a step, and what has to keep running.',
  },
  {
    title: 'Cut the scope',
    text: 'We agree the engagement and the first release before a long build starts.',
    detail:
      'That is one service: custom software development, application modernization, AI workflow implementation, or prototype to production. We name what ships, what is out, and how you will know it worked.',
  },
  {
    title: 'Build in view',
    text: 'You see working software in the environment your team will use.',
    detail:
      'Each release shows up where the work already happens. Decisions that would be expensive to undo are written down while they are still small.',
  },
  {
    title: 'Leave it runnable',
    text: 'You leave with software your team can run and release, and a way to review any AI step.',
    detail:
      'Handoff includes the repository, how it is deployed, and how your team checks an AI result when it is wrong.',
  },
];

export const stackNote =
  'We build with the stack the system already uses. That often means React, Angular, TypeScript, .NET, SQL Server, and Docker.';
